import React, { useEffect, useState, useRef } from 'react';
import StaffScanGate from '@/components/StaffScanGate';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Plus, Loader2, Check, X, Trash2, Package, Scan, Lock,
  ArrowRightCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue }
  from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow }
  from '@/components/ui/table';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';
import { useOperationDraft } from '@/hooks/useOperationDraft';
import { DraftsMenu } from '@/components/DraftsMenu';
import { ResumedDraftBanner } from '@/components/ResumedDraftBanner';

const BACK_ROUTE = '/sections/shirt-manufacturing';
const DEFAULT_CAT4 = 'Shirting';

const ShirtManufacturingBundleReceiptEntry = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState('shirting');

  return (
    <StaffScanGate stationName="Shirt Mfg — Bundle Receipt">
    <div className="min-h-screen bg-[#FFFBF5]" data-testid="shirt-mfg-bre">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center gap-4">
          <Button
            variant="ghost"
            onClick={() => navigate(BACK_ROUTE)}
            className="text-[#F9FAFB] hover:bg-white/10"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-xl font-bold font-display">Bundle Receipt Entry</h1>
            <p className="text-sm text-[#E8DCC8]">Shirt Manufacturing</p>
          </div>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-4 md:px-6 py-6">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList data-testid="bre-tabs">
            <TabsTrigger value="shirting" data-testid="tab-shirting">
              <Package className="w-4 h-4 mr-1" /> Shirting
            </TabsTrigger>
            <TabsTrigger value="shirts" data-testid="tab-shirts">
              <Scan className="w-4 h-4 mr-1" /> Shirts
            </TabsTrigger>
          </TabsList>
          <TabsContent value="shirting"><ShirtingTab /></TabsContent>
          <TabsContent value="shirts"><ShirtsTab /></TabsContent>
        </Tabs>
      </main>
    </div>
    </StaffScanGate>
  );
};

/* --------------------------------------------------------------
   TAB 1 — Shirting: cascading (Category → Design → Shade → Qty)
   -------------------------------------------------------------- */
const ShirtingTab = () => {
  const [designs, setDesigns] = useState([]);
  const [shades, setShades] = useState([]);
  const [design, setDesign] = useState('');
  const [shade, setShade] = useState('');
  const [qty, setQty] = useState('');
  const [bundleCode, setBundleCode] = useState('');
  const [consignmentId, setConsignmentId] = useState('');
  const [remarks, setRemarks] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [bundles, setBundles] = useState([]);

  useEffect(() => {
    apiClient
      .get('/shirt-mfg/item-master/designs', { params: { category4: DEFAULT_CAT4 } })
      .then((r) => setDesigns(r.data.designs || []))
      .catch(() => toast.error('Failed to load designs'));
    refreshBundles();
  }, []);

  useEffect(() => {
    if (!design) {
      setShades([]);
      setShade('');
      return;
    }
    apiClient
      .get('/shirt-mfg/item-master/shades', {
        params: { category4: DEFAULT_CAT4, design_name: design },
      })
      .then((r) => setShades(r.data.shades || []))
      .catch(() => toast.error('Failed to load shades'));
  }, [design]);

  const refreshBundles = () => {
    const params = consignmentId ? { consignment_id: consignmentId } : {};
    apiClient
      .get('/shirt-mfg/shirting-bundles', { params })
      .then((r) => setBundles(r.data.bundles || []))
      .catch(() => {});
  };

  useEffect(() => { refreshBundles(); }, [consignmentId]);  // eslint-disable-line

  const onSubmit = async () => {
    if (!design || !shade || !qty) {
      toast.error('Design, shade & qty required');
      return;
    }
    setSubmitting(true);
    try {
      const res = await apiClient.post('/shirt-mfg/shirting-bundles', {
        consignment_id: consignmentId || null,
        category4: DEFAULT_CAT4,
        design_name: design,
        shade,
        declared_qty: parseFloat(qty),
        bundle_code: bundleCode || null,
        remarks: remarks || null,
      });
      toast.success(`Bundle ${res.data.bundle_code} created`);
      setQty('');
      setBundleCode('');
      setRemarks('');
      refreshBundles();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Create failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card className="mt-4" data-testid="shirting-tab-card">
      <CardHeader>
        <CardTitle className="text-base">New Shirting Bundle</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <Label>Category</Label>
            <Input value={DEFAULT_CAT4} disabled data-testid="shirting-category-input" />
          </div>
          <div>
            <Label>Design Name *</Label>
            <Select value={design} onValueChange={setDesign}>
              <SelectTrigger data-testid="shirting-design-trigger">
                <SelectValue placeholder={`${designs.length} designs…`} />
              </SelectTrigger>
              <SelectContent className="max-h-[300px]">
                {designs.map((d) => (
                  <SelectItem key={d} value={d} data-testid={`design-opt-${d}`}>
                    {d}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Shade *</Label>
            <Select value={shade} onValueChange={setShade} disabled={!design}>
              <SelectTrigger data-testid="shirting-shade-trigger">
                <SelectValue placeholder={design ? 'Select shade…' : 'Pick design first'} />
              </SelectTrigger>
              <SelectContent>
                {shades.map((s) => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Declared Qty *</Label>
            <Input
              type="number" value={qty}
              onChange={(e) => setQty(e.target.value)}
              placeholder="e.g. 100"
              data-testid="shirting-qty-input"
            />
          </div>
          <div>
            <Label>Bundle Code</Label>
            <Input
              value={bundleCode}
              onChange={(e) => setBundleCode(e.target.value.toUpperCase())}
              placeholder="Auto-generated if blank"
              data-testid="shirting-bundle-code-input"
            />
          </div>
          <div>
            <Label>Consignment ID</Label>
            <Input
              value={consignmentId}
              onChange={(e) => setConsignmentId(e.target.value)}
              placeholder="CNS-..."
              data-testid="shirting-consignment-input"
            />
          </div>
          <div className="sm:col-span-2">
            <Label>Remarks</Label>
            <Input
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              placeholder="Optional"
              data-testid="shirting-remarks-input"
            />
          </div>
        </div>
        <div className="flex justify-end">
          <Button
            onClick={onSubmit}
            disabled={submitting || !design || !shade || !qty}
            className="bg-[#2D5F4F] hover:bg-[#234A3D]"
            data-testid="shirting-submit-btn"
          >
            {submitting
              ? <Loader2 className="w-4 h-4 animate-spin mr-2" />
              : <Plus className="w-4 h-4 mr-2" />}
            Create Bundle
          </Button>
        </div>

        <div className="pt-4 border-t">
          <div className="flex items-center justify-between mb-2">
            <CardTitle className="text-sm">Recent Shirting Bundles</CardTitle>
            <Badge variant="outline" data-testid="shirting-bundle-count">
              {bundles.length} bundles
            </Badge>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Bundle</TableHead>
                <TableHead>Design</TableHead>
                <TableHead>Shade</TableHead>
                <TableHead>Declared</TableHead>
                <TableHead>Actual</TableHead>
                <TableHead>Variance</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {!bundles.length && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-gray-400 py-6">
                    No bundles yet
                  </TableCell>
                </TableRow>
              )}
              {bundles.slice(0, 50).map((b) => (
                <TableRow key={b.bundle_code} data-testid={`shirting-row-${b.bundle_code}`}>
                  <TableCell className="font-mono text-xs">{b.bundle_code}</TableCell>
                  <TableCell className="text-xs">{b.design_name}</TableCell>
                  <TableCell className="text-xs">{b.shade}</TableCell>
                  <TableCell>{b.declared_qty}</TableCell>
                  <TableCell>{b.actual_qty ?? '—'}</TableCell>
                  <TableCell>{b.variance ?? '—'}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="text-[10px]">{b.status}</Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
};

/* --------------------------------------------------------------
   TAB 2 — Shirts: select COMPLETED JW → open box → scan → close
   -------------------------------------------------------------- */
const ShirtsTab = () => {
  const [orders, setOrders] = useState([]);
  const [jwId, setJwId] = useState('');
  const [openBoxes, setOpenBoxes] = useState([]);
  const [currentBox, setCurrentBox] = useState(null);
  const [newBoxBarcode, setNewBoxBarcode] = useState('');
  const [usnInput, setUsnInput] = useState('');
  const usnInputRef = useRef(null);
  const pendingBoxRef = useRef(null);
  const [resumedDraft, setResumedDraft] = useState(null);

  const {
    drafts, activeDraftId, saving, lastSavedAt,
    autoSave, clearDraft, loadDraft, removeDraft, setActiveDraftId,
  } = useOperationDraft({ moduleKey: 'section:Shirt Manufacturing:shirts', operationType: 'bundle_receipt', allowZero: true });

  // Box scans persist server-side, so the draft is a resumable bookmark of the
  // JW order + open box + scanned USN count.
  useEffect(() => {
    if (!jwId || !currentBox || currentBox.status !== 'OPEN') return;
    autoSave({
      label: `${jwId} · Box ${currentBox.box_barcode}`,
      bundleCount: currentBox.usn_count || 0,
      documentId: jwId,
      documentNo: jwId,
      payload: { jwId, boxBarcode: currentBox.box_barcode },
    });
  }, [jwId, currentBox, autoSave]);

  const handlePickJw = (id) => {
    const existing = (drafts || []).find((d) => d.document_id === id);
    if (existing) { handleResumeDraft(existing.draft_id); return; }
    setResumedDraft(null);
    setActiveDraftId(null);
    setJwId(id);
  };

  const handleResumeDraft = async (draftId) => {
    try {
      const d = await loadDraft(draftId);
      pendingBoxRef.current = d.payload?.boxBarcode || null;
      if (d.payload?.jwId) setJwId(d.payload.jwId);
      const summary = (drafts || []).find((x) => x.draft_id === draftId) || null;
      setResumedDraft(summary);
      toast.success(summary?.last_updated_by_name ? `Resumed draft — last worked on by ${summary.last_updated_by_name}` : 'Draft restored');
    } catch {
      toast.error('Failed to restore draft');
    }
  };

  const handleStartFresh = async () => {
    const id = resumedDraft?.draft_id;
    if (id) {
      const ok = await removeDraft(id);
      if (!ok) { toast.error('Could not discard the shared draft — please try again'); return; }
    }
    setResumedDraft(null);
    setJwId('');
    setCurrentBox(null);
    setOpenBoxes([]);
  };

  useEffect(() => {
    apiClient
      .get('/shirt-mfg/jw-orders', { params: { status: 'COMPLETED' } })
      .then((r) => setOrders(r.data.orders || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!jwId) {
      setOpenBoxes([]); setCurrentBox(null);
      return;
    }
    apiClient
      .get('/shirt-mfg/assortment/boxes', { params: { jw_id: jwId } })
      .then((r) => {
        const boxes = r.data.boxes || [];
        setOpenBoxes(boxes);
        // Prefer the exact box from a restored draft, else first OPEN box.
        const wanted = pendingBoxRef.current;
        pendingBoxRef.current = null;
        const target = (wanted && boxes.find((b) => b.box_barcode === wanted && b.status === 'OPEN'))
          || boxes.find((b) => b.status === 'OPEN');
        setCurrentBox(target || null);
      })
      .catch(() => {});
  }, [jwId]);

  const openBox = async () => {
    if (!newBoxBarcode || !jwId) {
      toast.error('Pick JW + enter box barcode');
      return;
    }
    try {
      const res = await apiClient.post('/shirt-mfg/assortment/boxes', {
        jw_id: jwId, box_barcode: newBoxBarcode.toUpperCase(),
      });
      toast.success(`Box ${res.data.box_barcode} opened`);
      setNewBoxBarcode('');
      setCurrentBox(res.data);
      setOpenBoxes((prev) => [res.data, ...prev]);
      setTimeout(() => usnInputRef.current?.focus(), 100);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Open box failed');
    }
  };

  const scanUsn = async () => {
    if (!usnInput.trim() || !currentBox) return;
    const u = usnInput.trim().toUpperCase();
    try {
      const res = await apiClient.post('/shirt-mfg/assortment/scan-usn', {
        box_barcode: currentBox.box_barcode, usn_code: u,
      });
      toast.success(`Scanned — ${res.data.usn_count} USNs`);
      setCurrentBox({
        ...currentBox,
        usn_codes: [...(currentBox.usn_codes || []), u],
        usn_count: (currentBox.usn_count || 0) + 1,
      });
      setUsnInput('');
      usnInputRef.current?.focus();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Scan rejected');
      setUsnInput('');
      usnInputRef.current?.focus();
    }
  };

  const closeBox = async () => {
    if (!currentBox) return;
    try {
      await apiClient.post(
        `/shirt-mfg/assortment/boxes/${currentBox.box_barcode}/close`,
      );
      toast.success(`Box ${currentBox.box_barcode} closed`);
      setCurrentBox(null);
      clearDraft();
      setResumedDraft(null);
      // refresh
      const r = await apiClient.get('/shirt-mfg/assortment/boxes', {
        params: { jw_id: jwId },
      });
      setOpenBoxes(r.data.boxes || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Close failed');
    }
  };

  const transferToSfg = async () => {
    const ready = openBoxes
      .filter((b) => b.status === 'READY_FOR_INTERNAL_TRANSFER')
      .map((b) => b.box_barcode);
    if (!ready.length) {
      toast.error('No ready boxes');
      return;
    }
    try {
      const res = await apiClient.post(
        '/shirt-mfg/assortment/transfer-to-sfg',
        { box_barcodes: ready, remarks: 'From Shirts BRE' },
      );
      toast.success(`Transferred ${res.data.transferred} box(es) to SFG`);
      const r = await apiClient.get('/shirt-mfg/assortment/boxes', {
        params: { jw_id: jwId },
      });
      setOpenBoxes(r.data.boxes || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Transfer failed');
    }
  };

  return (
    <Card className="mt-4" data-testid="shirts-tab-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">
            Job Receipt — Pack Shirts into Bundles
          </CardTitle>
          <DraftsMenu
            drafts={drafts}
            onResume={handleResumeDraft}
            onDelete={removeDraft}
            activeDraftId={activeDraftId}
            saving={saving}
            lastSavedAt={lastSavedAt}
            variant="light"
          />
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {resumedDraft && (
          <ResumedDraftBanner draft={resumedDraft} onStartFresh={handleStartFresh} />
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label>Job Work Order (COMPLETED)</Label>
            <Select value={jwId} onValueChange={handlePickJw}>
              <SelectTrigger data-testid="shirts-jw-trigger">
                <SelectValue placeholder="Select JW order…" />
              </SelectTrigger>
              <SelectContent>
                {!orders.length && (
                  <SelectItem disabled value="none">
                    No COMPLETED JW orders
                  </SelectItem>
                )}
                {orders.map((o) => (
                  <SelectItem key={o.jw_id} value={o.jw_id}>
                    {o.jw_id} • {o.assembly_item} • rec {o.received_qty}
                    {(drafts || []).some((d) => d.document_id === o.jw_id) && (
                      <span className="ml-2 font-semibold text-amber-600">· Draft</span>
                    )}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>New Box Barcode</Label>
            <div className="flex gap-2">
              <Input
                value={newBoxBarcode}
                onChange={(e) => setNewBoxBarcode(e.target.value.toUpperCase())}
                placeholder="BOX-SHM-..."
                disabled={!jwId}
                data-testid="shirts-new-box-input"
              />
              <Button
                onClick={openBox}
                disabled={!jwId || !newBoxBarcode}
                className="bg-[#2D5F4F] hover:bg-[#234A3D]"
                data-testid="shirts-open-box-btn"
              >
                <Plus className="w-4 h-4 mr-1" /> Open
              </Button>
            </div>
          </div>
        </div>

        {currentBox && currentBox.status === 'OPEN' && (
          <div className="border rounded-lg p-4 bg-emerald-50/50">
            <div className="flex items-center justify-between mb-2">
              <div>
                <div className="font-mono text-sm">Box: {currentBox.box_barcode}</div>
                <div className="text-xs text-gray-500">
                  {currentBox.usn_count || 0} USN(s) scanned
                </div>
              </div>
              <Button
                size="sm" variant="outline"
                onClick={closeBox}
                disabled={!currentBox.usn_count}
                data-testid="shirts-close-box-btn"
              >
                <Lock className="w-4 h-4 mr-1" /> Close Box
              </Button>
            </div>
            <div className="flex gap-2">
              <Input
                ref={usnInputRef}
                value={usnInput}
                onChange={(e) => setUsnInput(e.target.value.toUpperCase())}
                onKeyDown={(e) => e.key === 'Enter' && scanUsn()}
                placeholder="Scan USN…"
                data-testid="shirts-usn-input"
                autoFocus
              />
              <Button
                onClick={scanUsn} disabled={!usnInput.trim()}
                className="bg-[#2D5F4F] hover:bg-[#234A3D]"
                data-testid="shirts-scan-btn"
              >
                <Scan className="w-4 h-4 mr-1" /> Scan
              </Button>
            </div>
          </div>
        )}

        <div className="pt-2">
          <div className="flex items-center justify-between mb-2">
            <CardTitle className="text-sm">Boxes for this JW</CardTitle>
            <Button
              size="sm"
              onClick={transferToSfg}
              className="bg-[#1A1F2E] hover:bg-[#0F1320] text-white"
              data-testid="shirts-transfer-sfg-btn"
            >
              <ArrowRightCircle className="w-4 h-4 mr-1" /> Transfer Ready Boxes to SFG
            </Button>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Box</TableHead>
                <TableHead>USNs</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Created</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {!openBoxes.length && (
                <TableRow>
                  <TableCell colSpan={4} className="text-center text-gray-400 py-6">
                    {jwId ? 'No boxes yet for this JW' : 'Pick a JW order first'}
                  </TableCell>
                </TableRow>
              )}
              {openBoxes.map((b) => (
                <TableRow key={b.box_barcode} data-testid={`shirts-box-row-${b.box_barcode}`}>
                  <TableCell className="font-mono text-xs">{b.box_barcode}</TableCell>
                  <TableCell>{b.usn_count || 0}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="text-[10px]">
                      {b.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-xs text-gray-500">
                    {b.created_at && new Date(b.created_at).toLocaleDateString('en-IN')}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
};

export default ShirtManufacturingBundleReceiptEntry;
